/**
 * @file tool-settings.ts
 * @description Provides stateless utility functions for managing, persisting, and syncing tool settings.
 * Following AGENTS.md rules, this module does not maintain internal global state.
 * #2098: Per-platform storage — each runtime (tauri/pwa/mobile/desktop) gets its own localStorage key.
 */

// --- Platform Detection (inline, no import from apps/) ---

export type PlatformKey = 'tauri' | 'pwa' | 'mobile' | 'desktop';

export function getPlatformKey(): PlatformKey {
    if (typeof window === 'undefined') return 'desktop';
    if ((window as any).__TAURI__ || (window as any).__TAURI_INTERNALS__) return 'tauri';
    if (window.matchMedia?.('(display-mode: standalone)').matches) return 'pwa';
    if (window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) return 'mobile';
    return 'desktop';
}

const STORAGE_KEY = (): string => `hcie_tool_settings_${getPlatformKey()}`;

// ---

/**
 * @interface SyncableState
 * @description Represents a flat state object used by the UI/Core to sync with structured tool settings.
 */
export interface SyncableState {
  current_tool?: { id: string };
  pen_color: string;
  pen_width: number;
  pen_opacity: number;
  brush_hardness: number;
  brush_flow: number;
  spray_radius: number;
  spray_density: number;
  fill_tolerance: number;
  text_size: number;
  text_font: string;
  shape_fill: boolean;
  round_rect_corner_radius: number;
}

/**
 * @interface ToolSettings
 * @description Represents the structured, persistable configuration for all drawing tools.
 */
export interface ToolSettings {
  lastToolId: string;
  general: {
    color: string;
  };
  pen:    { size: number; opacity: number; };
  brush:  { size: number; opacity: number; hardness: number; flow: number; };
  eraser: { size: number; opacity: number; hardness: number; };
  spray:  { radius: number; density: number; opacity: number; };
  fill:   { tolerance: number; opacity: number; };
  text:   { size: number; font: string; };
  shapes: { fill: boolean; cornerRadius: number; size: number; opacity: number; };
}

/**
 * @constant DEFAULT_TOOL_SETTINGS
 * @description Default fallback values for all tool configurations.
 */
export const DEFAULT_TOOL_SETTINGS: ToolSettings = {
  lastToolId: 'btn-pen',
  general: {
    color: '#000000',
  },
  pen:    { size: 2,  opacity: 100 },
  brush:  { size: 10, opacity: 100, hardness: 80, flow: 100 },
  eraser: { size: 15, opacity: 100, hardness: 50 },
  spray:  { radius: 20, density: 30, opacity: 100 },
  fill:   { tolerance: 32, opacity: 100 },
  text:   { size: 40, font: 'Roboto' },
  shapes: { fill: false, cornerRadius: 10, size: 2, opacity: 100 },
};

/**
 * @function createMergedToolSettings
 * @description Pure function that merges stored settings with defaults to ensure version compatibility.
 * @param {Partial<ToolSettings> | null} storedSettings - Parsed settings from storage.
 * @returns {ToolSettings} A complete ToolSettings object.
 */
export function createMergedToolSettings(storedSettings: Partial<ToolSettings> | null): ToolSettings {
  if (!storedSettings) {
    return JSON.parse(JSON.stringify(DEFAULT_TOOL_SETTINGS));
  }

  return {
    lastToolId: storedSettings.lastToolId || DEFAULT_TOOL_SETTINGS.lastToolId,
    general: { ...DEFAULT_TOOL_SETTINGS.general, ...storedSettings.general },
    pen:     { ...DEFAULT_TOOL_SETTINGS.pen,     ...storedSettings.pen },
    brush:   { ...DEFAULT_TOOL_SETTINGS.brush,   ...storedSettings.brush },
    eraser:  { ...DEFAULT_TOOL_SETTINGS.eraser,  ...storedSettings.eraser },
    spray:   { ...DEFAULT_TOOL_SETTINGS.spray,   ...storedSettings.spray },
    fill:    { ...DEFAULT_TOOL_SETTINGS.fill,    ...storedSettings.fill },
    text:    { ...DEFAULT_TOOL_SETTINGS.text,    ...storedSettings.text },
    shapes:  { ...DEFAULT_TOOL_SETTINGS.shapes,  ...storedSettings.shapes },
  };
}

/**
 * @function syncSettingsToState
 * @description Updates a flat state object with values from a structured ToolSettings object.
 */
export function syncSettingsToState(settings: ToolSettings, state: SyncableState): void {
  const toolId: string | undefined = state.current_tool?.id;

  // Apply general settings regardless of tool
  state.pen_color = settings.general.color;

  if (!toolId) {
    state.pen_width   = settings.pen.size;
    state.pen_opacity = settings.pen.opacity / 100;
    return;
  }

  // [CRITICAL_LOGIC_START]
  switch (toolId) {
    case 'btn-pen':
    case 'btn-line':
      state.pen_width   = settings.pen.size;
      state.pen_opacity = settings.pen.opacity / 100;
      break;
    case 'btn-brush':
      state.pen_width      = settings.brush.size;
      state.brush_hardness = settings.brush.hardness / 100;
      state.brush_flow     = settings.brush.flow / 100;
      state.pen_opacity    = settings.brush.opacity / 100;
      break;
    case 'btn-eraser':
      state.pen_width      = settings.eraser.size;
      state.brush_hardness = settings.eraser.hardness / 100;
      state.pen_opacity    = settings.eraser.opacity / 100;
      break;
    case 'btn-spray':
      state.spray_radius  = settings.spray.radius;
      state.spray_density = settings.spray.density;
      state.pen_opacity   = settings.spray.opacity / 100;
      break;
    case 'btn-flood-fill':
      state.fill_tolerance = settings.fill.tolerance;
      state.pen_opacity    = settings.fill.opacity / 100;
      break;
    case 'btn-text':
      state.text_size   = settings.text.size;
      state.text_font   = settings.text.font;
      state.pen_opacity = 1;
      break;
    case 'btn-rect':
    case 'btn-circle':
    case 'btn-rounded-rect':
    case 'btn-ellipse':
      state.pen_width                = settings.shapes.size;
      state.shape_fill               = settings.shapes.fill;
      state.round_rect_corner_radius = settings.shapes.cornerRadius;
      state.pen_opacity              = settings.shapes.opacity / 100;
      break;
  }
  // [CRITICAL_LOGIC_END]
}

/**
 * @function syncStateToSettings
 * @description Updates a structured ToolSettings object with current values from a flat state object.
 */
export function syncStateToSettings(state: SyncableState, settings: ToolSettings): void {
  const toolId: string | undefined = state.current_tool?.id;

  if (state.pen_color) settings.general.color = state.pen_color;
  if (toolId) settings.lastToolId = toolId;

  if (!toolId) return;

  // [CRITICAL_LOGIC_START]
  switch (toolId) {
    case 'btn-pen':
    case 'btn-line':
      settings.pen.size    = state.pen_width;
      settings.pen.opacity = Math.round(state.pen_opacity * 100);
      break;
    case 'btn-brush':
      settings.brush.size     = state.pen_width;
      settings.brush.hardness = Math.round(state.brush_hardness * 100);
      settings.brush.flow     = Math.round(state.brush_flow * 100);
      settings.brush.opacity  = Math.round(state.pen_opacity * 100);
      break;
    case 'btn-eraser':
      settings.eraser.size     = state.pen_width;
      settings.eraser.hardness = Math.round(state.brush_hardness * 100);
      settings.eraser.opacity  = Math.round(state.pen_opacity * 100);
      break;
    case 'btn-spray':
      settings.spray.radius  = state.spray_radius;
      settings.spray.density = state.spray_density;
      settings.spray.opacity = Math.round(state.pen_opacity * 100);
      break;
    case 'btn-flood-fill':
      settings.fill.tolerance = state.fill_tolerance;
      settings.fill.opacity   = Math.round(state.pen_opacity * 100);
      break;
    case 'btn-text':
      settings.text.size = state.text_size;
      settings.text.font = state.text_font;
      break;
    case 'btn-rect':
    case 'btn-circle':
    case 'btn-rounded-rect':
    case 'btn-ellipse':
      settings.shapes.size         = state.pen_width;
      settings.shapes.fill         = state.shape_fill;
      settings.shapes.cornerRadius = state.round_rect_corner_radius;
      settings.shapes.opacity      = Math.round(state.pen_opacity * 100);
      break;
  }
  // [CRITICAL_LOGIC_END]
}

/**
 * @function loadToolSettings
 * @description Loads tool settings from per-platform local storage.
 */
export function loadToolSettings(): ToolSettings {
  if (typeof window === 'undefined') return DEFAULT_TOOL_SETTINGS;

  try {
    const raw: string | null = localStorage.getItem(STORAGE_KEY());
    const stored: Partial<ToolSettings> | null = raw ? JSON.parse(raw) : null;
    return createMergedToolSettings(stored);
  } catch (error) {
    console.error('[Shared] Failed to load tool settings:', error);
    return JSON.parse(JSON.stringify(DEFAULT_TOOL_SETTINGS));
  }
}

/**
 * @function saveToolSettings
 * @description Persists tool settings to per-platform local storage.
 */
export function saveToolSettings(settings: ToolSettings): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY(), JSON.stringify(settings));
  } catch (error) {
    console.error('[Shared] Failed to save tool settings:', error);
  }
}
